import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

// Webcam selfie capture with a confirm step: live preview → capture a still →
// Retake / Use photo. Confirming hands a JPEG File to the caller (it is
// uploaded with the punch request itself — there is no separate upload call).
//
// The preview and the captured frame are both mirrored, matching what people
// expect from a front camera; an un-mirrored still next to a mirrored preview
// reads as "flipped".

const STATE = {
    STARTING: 'starting',
    LIVE: 'live',
    CAPTURED: 'captured',
    ERROR: 'error',
};

const SelfieCaptureModal = ({ isOpen, onClose, onCapture }) => {
    const [phase, setPhase] = useState(STATE.STARTING);
    const [errorMessage, setErrorMessage] = useState('');
    const [capturedBlob, setCapturedBlob] = useState(null);
    const [capturedUrl, setCapturedUrl] = useState(null);

    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
    }, []);

    const clearCapture = useCallback(() => {
        setCapturedBlob(null);
        setCapturedUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    }, []);

    // Start/stop the camera with the modal's lifecycle
    useEffect(() => {
        if (!isOpen) return undefined;

        let cancelled = false;
        setPhase(STATE.STARTING);
        setErrorMessage('');

        if (!navigator.mediaDevices?.getUserMedia) {
            setPhase(STATE.ERROR);
            setErrorMessage("This browser can't access the camera. Use “Upload” to pick a photo instead.");
            return undefined;
        }

        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
            .then(stream => {
                if (cancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) videoRef.current.srcObject = stream;
                setPhase(STATE.LIVE);
            })
            .catch(err => {
                if (cancelled) return;
                setPhase(STATE.ERROR);
                setErrorMessage(
                    err?.name === 'NotAllowedError'
                        ? 'Camera access was denied. Allow the camera for this site, or use “Upload” to pick a photo.'
                        : "Couldn't start the camera. Use “Upload” to pick a photo instead."
                );
            });

        return () => {
            cancelled = true;
            stopStream();
            clearCapture();
        };
    }, [isOpen, stopStream, clearCapture]);

    const handleCapture = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        // Mirror the frame to match the on-screen preview
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0);

        canvas.toBlob(blob => {
            if (!blob) return;
            setCapturedBlob(blob);
            setCapturedUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
            setPhase(STATE.CAPTURED);
        }, 'image/jpeg', 0.9);
    };

    const handleRetake = () => {
        clearCapture();
        setPhase(STATE.LIVE);
    };

    const handleConfirm = () => {
        if (!capturedBlob) return;
        onCapture(new File([capturedBlob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' }));
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/60" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:camera-outline" className="tw-text-base" />
                        {phase === STATE.CAPTURED ? 'Use this photo?' : 'Take a photo'}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600"
                        title="Close"
                    >
                        <Icon icon="mdi:close" />
                    </button>
                </div>

                {/* Viewport */}
                <div className="tw-relative tw-bg-gray-900 tw-aspect-[4/3]">
                    {/* Live video stays mounted (hidden behind the still) so Retake is instant */}
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`tw-w-full tw-h-full tw-object-cover ${phase === STATE.CAPTURED ? 'tw-hidden' : ''}`}
                        style={{ transform: 'scaleX(-1)' }}
                    />
                    {phase === STATE.CAPTURED && capturedUrl && (
                        <img src={capturedUrl} alt="Captured selfie" className="tw-w-full tw-h-full tw-object-cover" />
                    )}
                    {phase === STATE.STARTING && (
                        <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-gray-300">
                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl" />
                            <span className="tw-text-xs">Starting camera…</span>
                        </div>
                    )}
                    {phase === STATE.ERROR && (
                        <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-text-center">
                            <Icon icon="mdi:camera-off-outline" className="tw-text-3xl tw-text-gray-400" />
                            <p className="tw-text-xs tw-text-gray-300 tw-m-0">{errorMessage}</p>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-py-4">
                    {phase === STATE.LIVE && (
                        <button
                            type="button"
                            onClick={handleCapture}
                            className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2.5 tw-rounded-full tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:camera" className="tw-text-lg" /> Capture
                        </button>
                    )}
                    {phase === STATE.CAPTURED && (
                        <>
                            <button
                                type="button"
                                onClick={handleRetake}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-600 tw-text-sm tw-font-medium hover:tw-bg-gray-50"
                            >
                                <Icon icon="mdi:camera-retake-outline" /> Retake
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-md tw-bg-emerald-500 tw-text-white tw-text-sm tw-font-medium hover:tw-bg-emerald-600"
                            >
                                <Icon icon="mdi:check" /> Use photo
                            </button>
                        </>
                    )}
                    {(phase === STATE.STARTING || phase === STATE.ERROR) && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="tw-px-4 tw-py-2 tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-600 tw-text-sm tw-font-medium hover:tw-bg-gray-50"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SelfieCaptureModal;
